import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AgGrid, Banner, Card, Flex, Grid, SegmentedControl, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useAppContext } from "~/contexts/appContext";
import { REPORTING_PAGE_SIZE } from "~/pages/Reporting/constants";
import {
  getLatestVersion,
  getReportTypeLabel,
} from "~/pages/Reporting/ReportTemplateEditor/types";
import {
  STATUS_LABEL,
  TemplateVersionStatus,
} from "~/pages/Reporting/ReportTemplateViewer/components/TemplateVersionStatus";
import {
  caseInsensitiveComparator,
  dateFieldComparator,
  formatArchivedByLabel,
  formatUpdatedByLabel,
  formatUserName,
} from "~/pages/Reporting/utils";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import { useFetchTemplatesByArena } from "../../hooks";
import {
  gridContainer,
  statusCellWrapper,
  templateCardLayout,
  templateListHeader,
} from "../../styles_vanilla.css";
import { ReportTemplate } from "../../types";
import { NewTemplateButton } from "../NewTemplateButton";

type ViewFilter = "active" | "archived" | "system";

export type CustomTemplatesListProps = {
  arenaId: number;
  templates: ReportTemplate[];
};

const SEGMENT_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Archived", value: "archived" },
  { label: "System", value: "system" },
];

const EMPTY_STATE_CONFIG: Record<ViewFilter, { title: string; subtitle?: string }> = {
  active: {
    title: "No custom templates yet",
    subtitle:
      "Create a custom template from scratch or duplicate a system template to get started",
  },
  archived: {
    title: "No archived templates yet",
  },
  system: {
    title: "No system templates yet",
    subtitle: "Create a new system template to get started",
  },
};

const VALID_VIEW_FILTERS: ViewFilter[] = ["active", "archived", "system"];

type NoRowsOverlayProps = { title: string; subtitle?: string };

const NoRowsOverlay = ({ title, subtitle }: NoRowsOverlayProps) => (
  <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="16px">
    <Icon type="file-alt" size="xxx-large" color={Colors.Neutral600} />
    <Text variant="secondary-xxl">{title}</Text>
    {subtitle && <Text variant="secondary-md">{subtitle}</Text>}
  </Flex>
);

function parseViewFilter(search: string): ViewFilter {
  const raw = new URLSearchParams(search).get("view");
  return VALID_VIEW_FILTERS.includes(raw as ViewFilter) ? (raw as ViewFilter) : "active";
}

const StatusCellRenderer = (params: AgGrid.ICellRendererParams<ReportTemplate>) => {
  const { data, context } = params;
  if (!data) return null;

  const version = getLatestVersion(data.currentVersion, data.versions);
  const status = version?.status;
  if (!status) return <Text variant="secondary-sm">-</Text>;

  return (
    <Flex flexDirection="column" justifyContent="center" className={statusCellWrapper}>
      <TemplateVersionStatus
        versionStatus={status}
        statusByUser={data.updatedByUser ?? null}
        statusAt={data.updatedAt}
        formatDate={context?.formatDate}
        attributionPrefix="Updated by"
      />
    </Flex>
  );
};

export const CustomTemplatesList = ({ arenaId, templates }: CustomTemplatesListProps) => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAppContext();
  const showSystemTemplatesLink = hasGlobalReportingAccess(user.roles);
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const viewFilter = useMemo(() => parseViewFilter(location.search), [location.search]);

  const setViewFilter = useCallback(
    (value: ViewFilter) => {
      const params = new URLSearchParams(location.search);
      if (value === "active") {
        params.delete("view");
      } else {
        params.set("view", value);
      }
      const search = params.toString();
      history.push({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      });
    },
    [history, location.search, location.pathname],
  );

  const { templates: rawSystemTemplates, queryResult: systemQueryResult } =
    useFetchTemplatesByArena(null, { skip: viewFilter !== "system" });

  const systemTemplates = useMemo(
    () => rawSystemTemplates.filter((t) => t.isActive !== false),
    [rawSystemTemplates],
  );

  const activeTemplates = useMemo(
    () => templates.filter((t) => t.isActive !== false),
    [templates],
  );

  const archivedTemplates = useMemo(
    () => templates.filter((t) => t.isActive === false),
    [templates],
  );

  const displayedTemplates = useMemo(() => {
    if (viewFilter === "active") return activeTemplates;
    if (viewFilter === "archived") return archivedTemplates;
    return systemTemplates;
  }, [viewFilter, activeTemplates, archivedTemplates, systemTemplates]);

  const { gridOptions: baseGridOptions } = useAgGridDefaults({
    overrides: {
      paginationPageSize: REPORTING_PAGE_SIZE,
      pagination: true,
      rowHeight: 60,
    },
  });

  const formatDate = useCallback(
    (date: string) => formatDateWithTimezone(date, "MMM d, yyyy"),
    [formatDateWithTimezone],
  );

  const gridOptions = useMemo(
    () => ({
      ...baseGridOptions,
      overlayNoRowsTemplate: undefined,
      context: { formatDate },
    }),
    [baseGridOptions, formatDate],
  );

  const columnDefs = useMemo<AgGrid.ColDef<ReportTemplate>[]>(() => {
    if (viewFilter === "system") {
      return [
        {
          field: "name",
          headerName: "Template name",
          sortable: true,
          flex: 2,
          comparator: caseInsensitiveComparator,
        },
        {
          headerName: "Report type",
          sortable: true,
          filter: true,
          flex: 2,
          valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
            getReportTypeLabel(params.data?.reportType),
        },
        {
          headerName: "Data package",
          sortable: true,
          filter: true,
          flex: 2,
          valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
            params.data?.dataPackage?.name ?? "-",
        },
        {
          headerName: "Last updated",
          sortable: true,
          filter: true,
          sort: "desc",
          flex: 2,
          valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
            formatUpdatedByLabel(
              params.data?.updatedAt,
              formatUserName(params.data?.updatedByUser),
              formatDateWithTimezone,
            ),
          comparator: dateFieldComparator<ReportTemplate>((d) => d?.updatedAt),
        },
      ];
    }

    const isActive = viewFilter === "active";
    return [
      {
        field: "name",
        headerName: "Template name",
        sortable: true,
        sort: "asc" as const,
        flex: 2,
        comparator: caseInsensitiveComparator,
      },
      {
        headerName: "Report type",
        sortable: true,
        filter: true,
        flex: 2,
        valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
          getReportTypeLabel(params.data?.reportType),
      },
      {
        headerName: "Data package",
        sortable: true,
        filter: true,
        flex: 2,
        valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
          params.data?.dataPackage?.name ?? "-",
      },
      ...(isActive
        ? [
            {
              headerName: "Status",
              sortable: true,
              filter: true,
              flex: 2,
              cellRenderer: StatusCellRenderer,
              valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) => {
                const version = getLatestVersion(
                  params.data?.currentVersion,
                  params.data?.versions,
                );
                const status = version?.status;
                if (!status) return "-";
                return STATUS_LABEL[status] ?? "-";
              },
              comparator: dateFieldComparator<ReportTemplate>((d) => d?.updatedAt),
            },
          ]
        : [
            {
              headerName: "Archived by",
              sortable: true,
              filter: true,
              flex: 2,
              valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
                formatArchivedByLabel(
                  params.data?.archivedAt,
                  formatUserName(params.data?.archivedByUser),
                  formatDateWithTimezone,
                ),
              comparator: dateFieldComparator<ReportTemplate>((d) => d?.archivedAt),
            },
          ]),
    ];
  }, [formatDateWithTimezone, viewFilter]);

  const handleRowClick = useCallback(
    (event: AgGrid.RowClickedEvent<ReportTemplate>) => {
      if (!event.data) return;
      if (viewFilter === "system") {
        history.push(`/reporting/system/templates/${event.data.id}/view`, {
          sourceArenaId: arenaId,
        });
      } else {
        history.push(`/reporting/${arenaId}/templates/${event.data.id}/view`);
      }
    },
    [history, arenaId, viewFilter],
  );

  const isSystemLoading = viewFilter === "system" && systemQueryResult.loading;

  return (
    <Card className={templateCardLayout}>
      <Flex justifyContent="space-between" alignItems="center" className={templateListHeader}>
        <Text variant="h3">Custom templates</Text>
        <Flex gap={12} alignItems="center">
          <SegmentedControl
            value={viewFilter}
            onChange={(val) => setViewFilter(val as ViewFilter)}
            options={SEGMENT_OPTIONS}
          />
          <NewTemplateButton arenaId={arenaId} hasTemplates={activeTemplates.length > 0} />
        </Flex>
      </Flex>
      {viewFilter === "system" && (
        <Banner
          variant="information"
          title="System templates are read-only references that can serve as starters for your custom templates"
          description="Duplicate a system template to create an editable version you can customize for this client"
          {...(showSystemTemplatesLink && {
            buttonProps: {
              children: "Go to system templates",
              icon: "arrow-up-right-from-square",
              iconPosition: "right",
              onClick: () =>
                window.open("/canopy/reporting/settings", "_blank", "noopener,noreferrer"),
            },
          })}
        />
      )}
      {viewFilter === "system" && systemQueryResult?.error ? (
        <Banner
          variant="danger"
          title="We couldn't load system templates"
          description="There was a problem fetching system templates. Please try again later."
        />
      ) : (
        <Flex className={gridContainer}>
          <Grid<ReportTemplate>
            key={viewFilter}
            suppressServerSideFullWidthLoadingRow
            suppressNoRowsOverlay={isSystemLoading}
            reactiveCustomComponents
            noRowsOverlayComponent={NoRowsOverlay}
            noRowsOverlayComponentParams={EMPTY_STATE_CONFIG[viewFilter]}
            rowData={displayedTemplates}
            getRowId={(params) => String(params.data.id)}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            onRowClicked={handleRowClick}
          />
        </Flex>
      )}
    </Card>
  );
};
